import { findCountryName } from "@redotech/locale/countries";
import { supplierIdFormat } from "@redotech/redo-inventory-id";
import { Provider } from "@redotech/redo-order-common/provider";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { DateFromDateOrIsoStringSchema } from "@redotech/zod-util/date-schema";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const LocationForMoveSchema_ = z.object({
  id: z.string().meta({ privacy: privacy.id.external.resource }),
  name: z.string().meta({ privacy: privacy.resourceLabel }),
  movable: z.boolean().nullish().meta({ privacy: privacy.flag }),
  message: z.string().nullish().meta({ privacy: privacy.text.thirdParty }),
  unavailableLineItemsCount: z
    .number()
    .nullish()
    .meta({ privacy: privacy.quantity }),
  selectedByFO: z.boolean().nullish().meta({ privacy: privacy.flag }),
  redoOnly: z.boolean().nullish().meta({ privacy: privacy.flag }),
});
export const LocationForMoveSchema: z.ZodType<LocationForMove> =
  LocationForMoveSchema_;
export interface LocationForMove extends z.infer<
  typeof LocationForMoveSchema_
> {}

const ShopifyLocationAddressSchema = z.object({
  address1: z
    .string()
    .nullish()
    .meta({ privacy: privacy.address_line.merchant }),
  address2: z
    .string()
    .nullish()
    .meta({ privacy: privacy.address_line.merchant }),
  city: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
  country: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
  countryCode: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
  phone: z.string().nullish().meta({ privacy: privacy.phone.merchant }),
  province: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
  provinceCode: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
  zip: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
  company: z
    .string()
    .nullish()
    .meta({ privacy: privacy.company_name.merchant }),
});

const ShopifyFulfillmentServiceSchema_ = z.object({
  id: z.string().meta({ privacy: privacy.id.external.resource }),
  type: z.string().nullish().meta({ privacy: privacy.enumOrStatus }),
  serviceName: z.string().nullish().meta({ privacy: privacy.resourceLabel }),
});
export const ShopifyFulfillmentServiceSchema: z.ZodType<ShopifyFulfillmentService> =
  ShopifyFulfillmentServiceSchema_;
export interface ShopifyFulfillmentService extends z.infer<
  typeof ShopifyFulfillmentServiceSchema_
> {}

const ShopifyLocationSchema_ = z.object({
  id: z.string().meta({ privacy: privacy.id.external.resource }),
  name: z.string().meta({ privacy: privacy.resourceLabel }),
  address: ShopifyLocationAddressSchema.meta({ privacy: privacy.structured }),
  addressVerified: z.boolean().meta({ privacy: privacy.flag }),
  createdAt: z.string().meta({ privacy: privacy.timestamp }),
  fulfillmentService: ShopifyFulfillmentServiceSchema_.nullish().meta({
    privacy: privacy.structured,
  }),
  fulfillsOnlineOrders: z.boolean().meta({ privacy: privacy.flag }),
  isActive: z.boolean().meta({ privacy: privacy.flag }),
  shipsInventory: z.boolean().meta({ privacy: privacy.flag }),
  updatedAt: z.string().meta({ privacy: privacy.timestamp }),
});
export const ShopifyLocationSchema: z.ZodType<ShopifyFulfillmentLocation> =
  ShopifyLocationSchema_;

export interface ShopifyFulfillmentLocation extends z.infer<
  typeof ShopifyLocationSchema_
> {}

const RedoFulfillmentLocationSchema_ = ShopifyLocationSchema_.omit({
  id: true,
}).extend({
  _id: zExt.objectId().nullish().meta({ privacy: privacy.id.internal }),
  provider: createEnum(Provider, { enumName: "Provider" }).meta({
    privacy: privacy.enumOrStatus,
  }),
  externalId: z.string().meta({ privacy: privacy.id.external.resource }),
  teamId: z.string().meta({ privacy: privacy.id.internal }),
  providerCreatedAt: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.timestamp,
  }),
  providerUpdatedAt: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.timestamp,
  }),
  updatedAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.timestamp }),
  createdAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.timestamp }),
  canonicalLocationExternalId: z
    .string()
    .nullish()
    .meta({ privacy: privacy.id.external.resource }),
  shippingName: z
    .string()
    .nullish()
    .meta({ privacy: privacy.company_name.merchant }),
  supplierId: supplierIdFormat.schema
    .nullish()
    .meta({ privacy: privacy.id.external.resource }),
});
export const RedoFulfillmentLocationSchema: z.ZodType<RedoFulfillmentLocation> =
  RedoFulfillmentLocationSchema_;

export interface RedoFulfillmentLocation extends z.infer<
  typeof RedoFulfillmentLocationSchema_
> {}

export interface IRedoFulfillmentLocation extends z.ZodType<RedoFulfillmentLocation> {}

const CreateRedoFulfillmentLocationSchema_ =
  RedoFulfillmentLocationSchema_.omit({
    _id: true,
    updatedAt: true,
    createdAt: true,
  });
export const CreateRedoFulfillmentLocationSchema: z.ZodType<CreateRedoFulfillmentLocation> =
  CreateRedoFulfillmentLocationSchema_;

export interface CreateRedoFulfillmentLocation extends z.infer<
  typeof CreateRedoFulfillmentLocationSchema_
> {}

export function mapShopifyFulfillmentLocation(
  location: ShopifyFulfillmentLocation,
  teamId: string,
): CreateRedoFulfillmentLocation {
  const data = {
    ...location,
    provider: Provider.SHOPIFY,
    externalId: location.id,
    teamId,
    providerCreatedAt: new Date(location.createdAt),
    providerUpdatedAt: new Date(location.updatedAt),
    address: location.address
      ? {
          ...location.address,
          // Shopify often omits the full country name; derive it from countryCode
          // so the `country` column is never blank on new records.
          country:
            location.address.country ??
            (location.address.countryCode
              ? (findCountryName(location.address.countryCode) ?? null)
              : null),
        }
      : location.address,
  };
  return CreateRedoFulfillmentLocationSchema_.parse(data);
}
